import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  MessageSquare, Users, Smile, Palette, Wrench,
  PanelRightClose, PanelRight, TrendingUp,
  Sparkles, Hand, Clock, Crown, Zap
} from 'lucide-react';
import { sideBarModes } from '../../utils/common';
import { useMeetingAppContext } from '../../MeetingAppContextDef';
import { useSiteContext } from '../../context/SiteContext';
import { useBreakoutRooms } from '../../context/BreakoutRoomContext';
import { useAgoraChat } from '../../context/AgoraChatContext';
import { useMeetingState } from '../../context/MeetingStateContext';
import BrandingPanel from './panels/BrandingPanel';
import CallDurationTimer from './CallDurationTimer';
import ChatPanel from './panels/ChatPanel';
import ImpactPanel from './panels/ImpactPanel';
import ParticipantsPanel from './panels/ParticipantsPanel';
import ToolsPanel from './panels/ToolsPanel';
import WalletWidget from './wallet/WalletWidget';
import { BreakoutRoomModal, BreakoutRoomBanner } from './breakout';

// Standard reactions - tile badge only (+1 point)
const STANDARD_REACTIONS = [
  { id: 'clap', emoji: '👏', label: 'Clap', points: 1 },
  { id: 'thumbs_up', emoji: '👍', label: 'Thumbs Up', points: 1 },
  { id: 'heart', emoji: '❤️', label: 'Heart', points: 1 },
  { id: 'laugh', emoji: '😂', label: 'Laugh', points: 1 },
  { id: 'wow', emoji: '😮', label: 'Wow', points: 1 },
  { id: 'celebrate', emoji: '🎉', label: 'Celebrate', points: 1 },
];

// Boosted reactions - tile badge + floating animation (+3 points)
const BOOSTED_REACTIONS = [
  { id: 'rocket', emoji: '🚀', label: 'Rocket', points: 3, boosted: true },
  { id: 'boom', emoji: '💥', label: 'Boom', points: 3, boosted: true },
  { id: 'fire', emoji: '🔥', label: 'Fire', points: 3, boosted: true },
];

// Full-screen effect reactions
const EFFECT_REACTIONS = [
  { id: 'balloon', emoji: '🎈', label: 'Balloon' },
  { id: 'confetti', emoji: '🎉', label: 'Confetti' },
  { id: 'hearts', emoji: '❤️', label: 'Hearts' },
  { id: 'applause', emoji: '👏', label: 'Applause' },
  { id: 'mind_blown', emoji: '🤯', label: 'Mind Blown' },
];

const tabs = [
  // { id: 'wallet', icon: Crown, label: 'Wallet' }, // Hidden
  { id: 'chat', icon: MessageSquare, label: 'Chat' },
  { id: 'participants', icon: Users, label: 'Attendees' },
  { id: 'reactions', icon: Smile, label: 'Reactions' },
  { id: 'branding', icon: Palette, label: 'Branding' },
  { id: 'tools', icon: Wrench, label: 'Tools' },
  // { id: 'impact', icon: TrendingUp, label: 'Impact' }, // Hidden
];

export default function StudioRightSidebar({
  isCollapsed,
  onToggleCollapse,
  sideBarMode,
  setSideBarMode,
  participantsCount = 0,
  unreadChat = 0,
  // Reactions props
  onTriggerEffect,
  onSendReaction,
  onRaiseHand,
  onToggleBRB,
  isHandRaised = false,
  isBRB = false,
  // Branding props
  branding = {},
  onBrandingChange,
  // Chat props
  localParticipantName = 'You',
  // Whiteboard props
  startWhiteboard,
  stopWhiteboard,
  isWhiteboardOpen,
  // AI Translation props (synced with bottom bar)
  translationEnabled,
  setTranslationEnabled,
  selectedLanguage,
  setSelectedLanguage,
  startTranslation,
  stopTranslation,
  // AI Captions props
  aiCaptionsEnabled,
  handleToggleAICaptions,
  // Participant actions
  onMuteAll,
  onUnmuteAll,
  onLowerAllHands,
  onLowerHand,
  // Waiting room
  waitingParticipants = [],
  onAdmitParticipant,
  onAdmitAllParticipants,
  onDenyParticipant,
  onSendMessageToWaiting,
  waitingRoomChatMessages = {},
  autoAdmitRegisteredInWaitingRoom,
  // AI Notetaker
  transcriptions,
  sttBotActive,
  onStartNotetaker,
  onStopNotetaker,
  // Participants list for breakout rooms — raw Map + IDs for live name resolution
  participantsMap,
  participantIds = [],
  // Call duration timer
  meetingStartTime,
  // Presenter access
  isPresenter = false,
}) {
  // Get waiting room participants from context
  const { participantApproveFunc } = useMeetingAppContext();
  const { meetingRole, meetingSettings, user, isCoHost } = useSiteContext();
  const isBasicPlan = user?.plan?.toLowerCase() === 'basic';

  // Breakout rooms context
  const {
    showBreakoutModal,
    setShowBreakoutModal,
    currentBreakoutRoom,
  } = useBreakoutRooms();
  const isChatAllowed = meetingSettings?.allowChat !== false;
  const isHost = ["moderator", "co-host", "host"].includes(meetingRole);
  const isWebinarAttendee = meetingSettings?.roomType === "Webinar" &&
    !["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole);
  const visibleTabs = tabs.filter(t => {
    if (t.id === 'chat' && !isChatAllowed) return false;
    if (t.id === 'branding' && !isHost) return false;
    if (t.id === 'tools' && !isHost && !isPresenter) return false;
    // Webinar attendees only see Chat and Reactions
    if (isWebinarAttendee && t.id !== 'chat' && t.id !== 'reactions') return false;
    return true;
  });
  const [activeTab, setActiveTab] = useState(isChatAllowed ? 'chat' : 'participants');
  const waitingCount = waitingParticipants?.length || 0;

  // Track unread chat messages.
  // Messages can arrive via three independent channels:
  //   1. Agora Chat SDK (web ↔ web)
  //   2. Socket.IO broadcast (e.g. iOS app, or Agora Chat fallback)
  //   3. Persisted chat history snapshot (late-join replay)
  // Counting only Agora Chat misses iOS-app messages, so we union all three by id.
  const { messages: chatMessages } = useAgoraChat();
  const { chatHistory, socketChatMessages } = useMeetingState();
  const totalUniqueChatCount = useMemo(() => {
    const ids = new Set();
    (chatMessages || []).forEach(m => { if (m?.id) ids.add(m.id); });
    (socketChatMessages || []).forEach(m => { if (m?.id) ids.add(m.id); });
    (chatHistory || []).forEach(m => { if (m?.id) ids.add(m.id); });
    return ids.size;
  }, [chatMessages, socketChatMessages, chatHistory]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const lastSeenCountRef = useRef(totalUniqueChatCount);
  // Chat is truly visible only when sidebar is expanded AND chat tab is active
  const isChatVisible = !isCollapsed && activeTab === 'chat';

  useEffect(() => {
    if (isChatVisible) {
      lastSeenCountRef.current = totalUniqueChatCount;
      setUnreadChatCount(0);
    } else if (totalUniqueChatCount > lastSeenCountRef.current) {
      setUnreadChatCount(totalUniqueChatCount - lastSeenCountRef.current);
    }
  }, [totalUniqueChatCount, isChatVisible]);

  // Resolve participant IDs to full objects for breakout rooms
  // Done here (close to the data source) to avoid Map lookup issues in child components
  const breakoutParticipants = useMemo(() => {
    if (!participantIds || !participantsMap) return [];
    return participantIds.map(id => {
      // Try every lookup strategy (Agora uses numeric UIDs)
      let entry = null;
      if (typeof participantsMap.get === 'function') {
        entry = participantsMap.get(id) || participantsMap.get(Number(id)) || participantsMap.get(String(id));
      }
      // Fallback: iterate the Map for a match
      if (!entry && typeof participantsMap.forEach === 'function') {
        participantsMap.forEach((val, key) => {
          if (!entry && String(key) === String(id)) entry = val;
        });
      }
      if (entry) {
        // Check metaData role first, then check dynamic co-host promotion
        let role = entry.metaData?.meetingRole || 'participant';
        if (role === 'participant' && isCoHost(id)) {
          role = 'co-host';
        }
        return {
          id,
          displayName: entry.displayName || entry.name || String(id),
          name: entry.displayName || entry.name || String(id),
          role,
          isLocal: !!entry.isLocal,
        };
      }
      return { id, displayName: String(id), name: String(id), role: isCoHost(id) ? 'co-host' : 'participant', isLocal: false };
    });
  }, [participantIds, participantsMap, isCoHost]);

  // Breakout rooms are now created dynamically in-session (no dashboard room fetch needed)

  // Shared transcript history for AI features (AI Notetaker and AI Assistant)
  const [transcriptHistory, setTranscriptHistory] = useState([]);
  const lastProcessedRef = useRef({ id: null, text: null, timestamp: 0 });
  const recentTextsRef = useRef([]);

  // Helper function for echo detection
  const isSimilarText = (text1, text2) => {
    if (!text1 || !text2) return false;
    const t1 = text1.toLowerCase().trim();
    const t2 = text2.toLowerCase().trim();
    if (t1 === t2) return true;
    if (t1.includes(t2) || t2.includes(t1)) return true;
    const words1 = t1.split(/\s+/).filter(w => w.length > 2);
    const words2 = t2.split(/\s+/).filter(w => w.length > 2);
    if (words1.length >= 3 && words2.length >= 3) {
      const overlap = words1.filter(w => words2.includes(w)).length;
      if (overlap / Math.min(words1.length, words2.length) > 0.7) return true;
    }
    return false;
  };

  // Process transcriptions into transcript history (shared between ToolsPanel and ChatPanel)
  useEffect(() => {
    if (transcriptions) {
      const newEntry = Array.isArray(transcriptions)
        ? transcriptions[transcriptions.length - 1]
        : transcriptions;

      if (newEntry && newEntry.text) {
        const entryId = newEntry.id || `${newEntry.participantId}-${newEntry.timestamp}`;
        const entryText = newEntry.text.trim();
        const now = Date.now();

        if (entryText.length < 3) return;
        if (lastProcessedRef.current.id === entryId && lastProcessedRef.current.text === entryText) return;
        if (now - lastProcessedRef.current.timestamp < 3000 && isSimilarText(lastProcessedRef.current.text, entryText)) return;

        recentTextsRef.current = recentTextsRef.current.filter(r => now - r.time < 5000);
        if (recentTextsRef.current.some(r => isSimilarText(r.text, entryText))) return;

        recentTextsRef.current.push({ text: entryText, time: now });
        lastProcessedRef.current = { id: entryId, text: entryText, timestamp: now };

        setTranscriptHistory(prev => {
          if (prev.some(t => t.id === entryId)) {
            return prev.map(t => t.id === entryId ? { ...t, text: entryText } : t);
          }
          if (prev.slice(-10).some(t => isSimilarText(t.text, entryText))) return prev;

          return [...prev, {
            id: entryId,
            time: newEntry.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            text: entryText,
            speaker: newEntry.participantName || 'Speaker'
          }].slice(-100);
        });
      }
    }
  }, [transcriptions]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'chat') {
      setSideBarMode(sideBarModes.CHAT);
    } else if (tabId === 'participants') {
      setSideBarMode(sideBarModes.PARTICIPANTS);
    } else {
      setSideBarMode(null);
    }
  };

  // ChatPanel is rendered always (see JSX below), hidden via display:none
  // when the sidebar is collapsed or another tab is active. Don't return
  // it from renderContent — that would cause unmount on tab change and
  // wipe the local message state, which is the chat-disappears bug.
  const renderContent = () => {
    switch (activeTab) {
      case 'participants':
        return (
          <ParticipantsPanel
            onMuteAll={onMuteAll}
            onUnmuteAll={onUnmuteAll}
            onLowerAllHands={onLowerAllHands}
            onLowerHand={onLowerHand}
            waitingParticipants={waitingParticipants}
            onAdmitParticipant={onAdmitParticipant}
            onAdmitAllParticipants={onAdmitAllParticipants}
            onDenyParticipant={onDenyParticipant}
            onSendMessageToWaiting={onSendMessageToWaiting}
            waitingRoomChatMessages={waitingRoomChatMessages}
            autoAdmitRegisteredInWaitingRoom={autoAdmitRegisteredInWaitingRoom}
          />
        );
      case 'reactions':
        const isReactionsAllowed = meetingSettings?.allowReactions !== false;
        const isRaiseHandAllowed = meetingSettings?.allowRaiseHand !== false;
        return (
          <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain max-h-[calc(100vh-200px)]">
            <div className="p-3 pb-16 space-y-3">
              {/* Emoji Reactions - only shown when reactions are allowed */}
              {isReactionsAllowed && (
                <>
                  {/* Standard Reactions */}
                  <div>
                    <span className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 block">Reactions</span>
                    <p className="text-xs text-gray-500 mb-1">Shows briefly on your video tile</p>
                    <div className="flex items-center justify-between bg-white/5 rounded-xl p-1.5">
                      {STANDARD_REACTIONS.map((reaction) => (
                        <button
                          key={reaction.id}
                          onClick={() => onSendReaction?.(reaction.id, reaction.emoji, false, reaction.points)}
                          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors group"
                          title={`${reaction.label} (+${reaction.points} pt)`}
                        >
                          <span className="text-xl group-hover:scale-125 transition-transform inline-block">
                            {reaction.emoji}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Boosted Reactions */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-[11px] text-gray-400 uppercase tracking-wider">Boosted (+3 pts)</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">Triggers floating animation</p>
                    <div className="flex items-center gap-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-1.5 border border-purple-500/20">
                      {BOOSTED_REACTIONS.map((reaction) => (
                        <button
                          key={reaction.id}
                          onClick={() => onSendReaction?.(reaction.id, reaction.emoji, true, reaction.points)}
                          className="flex-1 flex flex-col items-center gap-0.5 p-1.5 rounded-lg hover:bg-white/10 transition-colors group"
                          title={`${reaction.label} (+${reaction.points} pts)`}
                        >
                          <span className="text-2xl group-hover:scale-125 transition-transform inline-block">
                            {reaction.emoji}
                          </span>
                          <span className="text-[9px] text-purple-300">{reaction.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Full-Screen Effects */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-yellow-400" />
                      <span className="text-[11px] text-gray-400 uppercase tracking-wider">Effects</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">Full-screen animations visible to all</p>
                    <div className="grid grid-cols-5 gap-1 bg-white/5 rounded-xl p-1.5">
                      {EFFECT_REACTIONS.map((reaction) => (
                        <button
                          key={reaction.id}
                          onClick={() => onTriggerEffect?.(reaction.id)}
                          className="flex flex-col items-center gap-0.5 p-1.5 rounded-xl hover:bg-white/10 transition-colors group"
                          title={reaction.label}
                        >
                          <span className="text-xl group-hover:scale-125 transition-transform">
                            {reaction.emoji}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Status Section - always shown */}
              <div>
                <span className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 block">Status</span>
                <div className={`grid gap-2 ${isRaiseHandAllowed ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {isRaiseHandAllowed && (
                    <button
                      onClick={() => onRaiseHand?.()}
                      className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl transition-colors ${
                        isHandRaised
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      <Hand className="w-5 h-5" />
                      <span className="text-xs font-medium">
                        {isHandRaised ? 'Lower' : 'Raise Hand'}
                      </span>
                    </button>
                  )}
                  <button
                    onClick={() => onToggleBRB?.()}
                    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl transition-colors ${
                      isBRB
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <Clock className="w-5 h-5" />
                    <span className="text-xs font-medium text-center leading-tight">
                      {isBRB ? "I'm Back" : 'Be Right Back'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Status indicators info */}
              {(isHandRaised || isBRB) && (
                <div className="p-3 bg-white/5 rounded-xl">
                  <p className="text-xs text-gray-400">
                    {isHandRaised && <span className="block mb-1">✋ Your hand is raised - the host can see you</span>}
                    {isBRB && <span className="block">⏳ You're marked as away</span>}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      case 'wallet':
        return (
          <div className="flex-1 overflow-y-auto p-4">
            <WalletWidget />
          </div>
        );
      case 'branding':
        return <BrandingPanel branding={branding} onChange={onBrandingChange} />;
      case 'tools':
        return (
          <ToolsPanel
            onOpenWhiteboard={startWhiteboard}
            onCloseWhiteboard={stopWhiteboard}
            isWhiteboardOpen={isWhiteboardOpen}
            onOpenBreakoutRooms={() => setShowBreakoutModal(true)}
            onDisplayQuestion={() => console.log('Display question')}
            translationEnabled={translationEnabled}
            setTranslationEnabled={setTranslationEnabled}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            startTranslation={startTranslation}
            stopTranslation={stopTranslation}
            aiCaptionsEnabled={aiCaptionsEnabled}
            handleToggleAICaptions={handleToggleAICaptions}
            transcriptHistory={transcriptHistory}
            participants={participantIds}
            currentRoomId={meetingSettings?.roomId || meetingSettings?._id}
            userRooms={[]}
            sttBotActive={sttBotActive}
            onStartNotetaker={onStartNotetaker}
            onStopNotetaker={onStopNotetaker}
          />
        );
      case 'impact':
        return <ImpactPanel />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Collapsed strip — visible when isCollapsed. Kept as a sibling of
          the expanded view (rather than an early return) so the expanded
          tree, including ChatPanel, stays mounted across collapse/expand
          and preserves its message state. */}
      <div
        style={{ display: isCollapsed ? 'flex' : 'none' }}
        className="w-10 md:w-14 bg-[#001233]/95 backdrop-blur-xl border-l border-white/10 flex-col h-full"
      >
        <button
          onClick={onToggleCollapse}
          className="p-2.5 md:p-4 hover:bg-white/10 transition-colors border-b border-white/10"
        >
          <PanelRight className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
        </button>
        <div className="flex-1 flex flex-col items-center py-3 md:py-4 gap-1.5 md:gap-2">
          {visibleTabs.map(tab => {
            const TabIcon = tab.icon;
            const isBrandingDisabled = tab.id === 'branding' && isBasicPlan;
            return (
              <button
                key={tab.id}
                onClick={() => { if (isBrandingDisabled) return; onToggleCollapse(); handleTabClick(tab.id); }}
                className={`p-2 md:p-3 rounded-xl transition-colors relative ${isBrandingDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'}`}
                title={tab.label}
              >
                <TabIcon className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                {tab.id === 'chat' && unreadChatCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-[#6a1fbf] rounded-full text-xs flex items-center justify-center text-white">
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </span>
                )}
                {tab.id === 'participants' && participantsCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#6a1fbf] rounded-full text-xs flex items-center justify-center text-white">
                    {participantsCount}
                  </span>
                )}
                {tab.id === 'participants' && waitingCount > 0 && isHost && (
                  <span className="absolute -top-1.5 -left-1.5 min-w-6 h-6 px-1 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-bold animate-bounce ring-2 ring-red-300/60 shadow-lg shadow-red-500/40">
                    {waitingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Expanded sidebar — always mounted (hidden via display:none when
          collapsed) so ChatPanel below stays in the React tree and
          preserves its messages state across the collapse toggle. */}
      <div
        style={{ display: isCollapsed ? 'none' : 'flex' }}
        className="w-72 md:w-80 bg-[#001233]/95 backdrop-blur-xl border-l border-white/10 flex-col h-full"
      >
        {/* Breakout Room Banner (when participant is in a breakout room) */}
        {currentBreakoutRoom && <BreakoutRoomBanner />}

        {/* Header with collapse button */}
        <div className="p-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-white">
              {visibleTabs.find(t => t.id === activeTab)?.label}
            </span>
            {activeTab === 'chat' && (
              <p className="text-xs text-gray-400">{totalUniqueChatCount} messages</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {meetingStartTime && <CallDurationTimer startTime={meetingStartTime} />}
            <button
              onClick={onToggleCollapse}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <PanelRightClose className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {/* ChatPanel is always rendered to preserve its message state
              across tab switches. Hidden via display:none when not the
              active tab. Other panels remain conditionally rendered since
              they don't accumulate state that needs to survive. */}
          <div
            className="h-full flex-col"
            style={{ display: activeTab === 'chat' ? 'flex' : 'none' }}
          >
            <ChatPanel localParticipantName={localParticipantName} transcriptHistory={transcriptHistory} />
          </div>
          {activeTab !== 'chat' && renderContent()}
        </div>

      {/* Tab Bar */}
      <div className="border-t border-white/10 px-1 py-1.5 flex-shrink-0">
        <div className="flex justify-between items-center">
          {visibleTabs.map(tab => {
            const TabIcon = tab.icon;
            const isBrandingDisabled = tab.id === 'branding' && isBasicPlan;
            return (
              <button
                key={tab.id}
                onClick={() => { if (isBrandingDisabled) return; handleTabClick(tab.id); }}
                className={`relative flex flex-col items-center justify-center p-1 rounded-md transition-colors ${isBrandingDisabled
                  ? 'opacity-40 cursor-not-allowed text-gray-500'
                  : activeTab === tab.id
                  ? 'text-white bg-white/10'
                  : 'text-gray-500 hover:text-gray-300'
                  }`}
                style={{ minWidth: '38px' }}
                title={tab.label}
              >
                <TabIcon className="w-4 h-4" />
                <span className="text-[8px] leading-tight mt-0.5">{tab.label}</span>
                {/* Waiting room notification badge - only for hosts */}
                {tab.id === 'participants' && waitingCount > 0 && isHost && (
                  <span className="absolute -top-1 -left-1 min-w-5 h-5 px-1 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-bold animate-bounce ring-2 ring-red-300/60 shadow-md shadow-red-500/40">
                    {waitingCount}
                  </span>
                )}
                {/* Unread chat badge */}
                {tab.id === 'chat' && unreadChatCount > 0 && activeTab !== 'chat' && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 bg-[#6a1fbf] rounded-full text-[9px] flex items-center justify-center text-white">
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </span>
                )}
                {/* Participants count badge */}
                {tab.id === 'participants' && participantsCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#6a1fbf] rounded-full text-[9px] flex items-center justify-center text-white">
                    {participantsCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      </div>

      {/* Breakout Room Modal */}
      <BreakoutRoomModal
        isOpen={showBreakoutModal}
        onClose={() => setShowBreakoutModal(false)}
        participants={breakoutParticipants}
        userRooms={[]}
      />
    </>
  );
}
